import React, { FC, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import { Article } from 't2-graphql-types';
import { TerritorySectionTitle } from '../TerritorySectionTitle/TerritorySectionTitle';
import { TerritoryGridContainer } from '../TerritoryGridContainer/TerritoryGridContainer';
import { PromptCardV2Container } from '../PromptCardV2/PromptCardV2Container';
import { LoadMoreButton } from '../LoadMoreButton/LoadMoreButton';
import { SortingDropdown } from '../SortingDropdown/SortingDropdown';
import {
  PromptListPurpose,
  PROMPTS_SORTING_DROPDOWN_ITEMS,
  PromptsSortingDropdownKey,
} from '../PromptsListSection/constants';
import { PromptCardV2Loader } from '../PromptCardV2/PromptCardV2Loader';
import { TerritorySectionTitleLoader } from '../TerritorySectionTitle/TerritorySectionTitleLoader';
import { TerritoryVersionType } from '../../layouts/TerritoryPageLayout/TerritoryPageLayout';
import { TerritorySeeAllButton } from '../TerritorySeeAllButton/TerritorySeeAllButton';

type TTerritoryPromptsContentProps = {
  territoryId: string;
  sectionTitle: string;
  renderedPromptsWithLiveStatus: Article[];
  isCollapsedSection: boolean;
  hasMorePromptsToLoad: boolean;
  isLoading: boolean;
  isPinningEnabled: boolean;
  canManagePinnedArticles: boolean;
  showLoadMoreButton: boolean;
  hideSortingDropdown?: boolean;
  onLoadMoreWithToggle: () => void;
  onSortByChange: (sortBy: string) => void;
  sortBy: PromptsSortingDropdownKey;
  openPromptModal: () => void;
  isAuthLoading?: boolean;
  territorySlug: string;
  territoryVersion: TerritoryVersionType;
  hideSeeAllButton?: boolean;
  seeAllUrl?: string;
};

export const TerritoryPromptsContent: FC<TTerritoryPromptsContentProps> = ({
  territoryId,
  sectionTitle,
  renderedPromptsWithLiveStatus,
  isCollapsedSection,
  hasMorePromptsToLoad,
  isLoading,
  isPinningEnabled,
  canManagePinnedArticles,
  showLoadMoreButton,
  hideSortingDropdown = false,
  onLoadMoreWithToggle,
  onSortByChange,
  sortBy,
  isAuthLoading,
  hideSeeAllButton = false,
  seeAllUrl,
}) => {
  const sectionTitleRef = useRef<HTMLDivElement>(null);

  if (isLoading || isAuthLoading) {
    return (
      <div>
        {isLoading || isAuthLoading ? (
          <TerritorySectionTitleLoader />
        ) : (
          <TerritorySectionTitle>{sectionTitle}</TerritorySectionTitle>
        )}
        <TerritoryGridContainer>
          <>
            {Array.from({ length: 6 }, (_, index) => (
              <PromptCardV2Loader key={index} />
            ))}
          </>
        </TerritoryGridContainer>
      </div>
    );
  }
  return (
    <div className="mb-12">
      <div className="flex justify-between">
        <TerritorySectionTitle ref={sectionTitleRef}>{sectionTitle}</TerritorySectionTitle>
        {!hideSeeAllButton && !isEmpty(renderedPromptsWithLiveStatus) && (
          <div className="ml-auto mb-5">
            <TerritorySeeAllButton url={seeAllUrl} />
          </div>
        )}
        {!hideSortingDropdown && !isEmpty(renderedPromptsWithLiveStatus) && (
          <SortingDropdown
            items={PROMPTS_SORTING_DROPDOWN_ITEMS}
            onSortByChange={onSortByChange}
            purpose={PromptListPurpose.territoryPrompts}
            territoryId={territoryId}
            defaultSortBy={sortBy}
            dropdownMenuPosition="right"
            dropdownLabelClassName="border border-t2-neutral-grey03"
            dropdownLabelbackgroundColor="transparent"
          />
        )}
      </div>
      {!isLoading && isEmpty(renderedPromptsWithLiveStatus) ? null : (
        <>
          <TerritoryGridContainer>
            {renderedPromptsWithLiveStatus?.map((prompt) => (
              <PromptCardV2Container
                key={prompt.id}
                prompt={prompt}
                isPinningEnabled={isPinningEnabled}
                canManagePinnedArticles={canManagePinnedArticles}
              />
            ))}
          </TerritoryGridContainer>
          <div className="flex justify-center mt-4">
            {showLoadMoreButton && (
              <LoadMoreButton
                onClick={onLoadMoreWithToggle}
                hasMoreToLoad={hasMorePromptsToLoad}
                isCollapsedSection={isCollapsedSection}
                isLoading={isLoading}
                sectionTitleRef={sectionTitleRef}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};
